import { action } from 'mobx';
import * as React from 'react';
import {
  PageProps,
  PageToolEvents,
  RetrieveFocusType,
} from '../../../../constants';
import { InfinityRetrieve } from '../../../../models';
import { PurchaseTaxTemplate } from './PurchaseTax.template';
import { PurchaseTaxModel } from './PurchaseTax.model';
import { PageComponent } from '../../../../utils/layout';
import { ConfirmWarning } from '../../../../utils/confirm';
import { GridLayout } from '../../../../components/layout/GridLayout';

interface PurchaseTaxState {
  // search
  stdate: string;
  enddate: string;
  artcd: string;
  artnm: string;
  divicd: string;
  divinm: string;
  proof: string;
  searchQuery: string;

  // data
  data: Array<PurchaseTaxModel>;
  focused?: PurchaseTaxModel;

  // trail
  mijamt_tot: string;
  sndamt_tot: string;
  total: string;
  iamt_tot: string;
}

/**
 * 컨트롤러
 * @window w_tb_ca640w_02
 * @category 매입세금계산서[미지급현황]
 */
export class PurchaseTax extends PageComponent<PageProps, PurchaseTaxState>
  implements PageToolEvents {
  infinity?: InfinityRetrieve;

  grid: React.RefObject<GridLayout> = React.createRef();

  constructor(props: PageProps, context: any) {
    super(props, context);
    this.props.onMount && this.props.onMount(this);

    const today = new Date();

    const year = today.getFullYear(); // 년도

    let month:string | number = today.getMonth() + 2; // 월

    if (month < 10) {
      month = `0${month}`;
    }

    let date:string | number = today.getDate(); // 날짜

    if (date < 10) {
      date = `0${date}`;
    }

    // state 기본값 정의
    this.state = props.state || {
      // search
      stdate: '19700101',
      enddate: `${year}${month}${date}`,
      artcd: '',
      divicd: '',
      proof: '%',
      searchQuery: '',

      // data
      data: [],

      // trail
      mijamt_tot: '0',
      sndamt_tot: '0',
      total: '0',
      iamt_tot: '0',
    };
  }

  @action
  async onFirstOpenEvent() {
    this.onRetrieveEvent();
  }

  @action
  async onRetrieveEvent(type: RetrieveFocusType = RetrieveFocusType.DEFAULT) {
    const { actionStore: api } = this.props;

    // 무한 스크롤바 헬퍼 초기화
    this.infinity = new InfinityRetrieve(
      {
        stdate: this.state.stdate,
        enddate: this.state.enddate,
        artcd: this.state.artcd,
        divicd: this.state.divicd,
        proof: this.state.proof,
        as_nm: this.state.searchQuery,
      },
      (params) => api.retrieve(params),
      (items, next) => {
        this.setState({
          data: [
            ...this.state.data,
            ...items.map((x: any) => new PurchaseTaxModel(x)),
          ],
        }, next);
      },
      async () => {
        await this.SS({
          data: [],
        });

        await this.infinity?.retrieveAll();
        if (this.state.data && this.state.data?.length > 0) {
          await this.grid.current?.setFocus(0);
        }
      },
    );

    // 상단 조회 버튼을 누를때는 기존 배열 초기화
    this.setState({
      data: [],
    }, async () => {
      const index = await this.infinity?.retrieveTo(['mijcltnm', 'mijnum'],
        [this.state.focused?.mijcltnm, this.state.focused?.mijnum], type, true) || 0;
      if (this.state.data && this.state.data.length > index) {
        await this.grid.current?.setFocus(index);
      }

      this.SS({
        mijamt_tot: this.infinity?.box?.mijamt_tot || '0',
        sndamt_tot: this.infinity?.box?.sndamt_tot || '0',
        total: this.infinity?.box?.total || '0',
        iamt_tot: this.infinity?.box?.iamt_tot || '0',
      });
    });
  }

  @action
  async onPrintEvent() {
    const { actionStore: api } = this.props;
    if (this.state.data.length < 1) {
      ConfirmWarning.show('오류', '출력할 내역이 없습니다.');
      return;
    }

    await api.printWithElmanManager({
      stdate: this.state.stdate,
      enddate: this.state.enddate,
      artcd: this.state.artcd,
      divicd: this.state.divicd,
      proof: this.state.proof,
      as_nm: this.state.searchQuery,
    });
  }

  @action
  async onExcelEvent() {
    const { actionStore: api } = this.props;
    if (this.state.data.length < 1) {
      ConfirmWarning.show('오류', '엑셀전환할 내역이 없습니다.');
      return;
    }

    await api.excel({
      stdate: this.state.stdate,
      enddate: this.state.enddate,
      artcd: this.state.artcd,
      divicd: this.state.divicd,
      proof: this.state.proof,
      as_nm: this.state.searchQuery,
    });
  }

  @action
  onRowFocusEvent(item:PurchaseTaxModel) {
    this.setState({ focused: item });
  }

  @action
  onClickOpenBalEnrollment() {
    this.props.publicStore.go(
      '/w_tb_ca640',
      {
        date: this.state.focused?.mijdate,
        remark: this.state.focused?.remark,
      },
    );
  }

  render() {
    return (
      <PurchaseTaxTemplate
        scope={this}
        update={(state, callback) => this.setState(state, callback)}
      />
    );
  }
}
